/**
 * Files on an account, a deal or a note (CRM #7, 2026-09-28) — ONE component, mounted by the
 * account timeline (an "Attach a file" control) and the deal sheet (its Files section).
 *
 *   mountAttachments(box, {entity, customer_code, work_item_id?, writer, onChange?})
 *   attachButton({entity, customer_code, work_item_id?, onDone})  → a file-picker button element
 *   attachmentLink(a)   → the download link (withCompany: act-as / viewing-as reach the file too)
 *
 * Server rules: app/services/attachments.py (20 MB; nothing a browser could run; reach 404).
 */
import { escapeHtml, getIcon, showError, showSuccess } from '@isg/ui-kit';
import { salesApi } from '../services/sales-api.js';
import { withCompany } from '../company-switcher.js';
import { shortDate } from '../lib/fig.js';

const MAX_MB = 20;
const ACCEPT = '.pdf,.png,.jpg,.jpeg,.gif,.webp,.heic,.heif,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.odt,.ods,.rtf,'
  + '.csv,.txt,.eml,.msg,.zip,.step,.stp,.dwg,.dxf';

function size(n) {
  if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  if (n >= 1024) return `${Math.round(n / 1024)} KB`;
  return `${n} B`;
}

/** `/api/sales/work/attachments/<id>/download`, carrying act-as / viewing-as like every call. */
export function attachmentUrl(a, { inline = false } = {}) {
  const base = `${a.url}${inline ? '?inline=1' : ''}`;
  return withCompany(base);
}

export function attachmentLink(a) {
  const view = a.inline ? ` · <a href="${escapeHtml(attachmentUrl(a, { inline: true }))}" target="_blank" rel="noopener">view</a>` : '';
  return `<a href="${escapeHtml(attachmentUrl(a))}" download>${getIcon('download', 12)} ${escapeHtml(a.filename)}</a>
    <span class="sc-list-meta">${escapeHtml(size(a.size || 0))}</span>${view}`;
}

async function uploadAll(files, target) {
  let ok = 0;
  for (const file of files) {
    if (file.size > MAX_MB * 1024 * 1024) { showError(`${file.name} is bigger than ${MAX_MB} MB.`); continue; }
    try {
      await salesApi.uploadAttachment({ ...target, file });
      ok += 1;
    } catch (err) {
      showError(`${file.name}: ${err.message || 'not attached'}`);
    }
  }
  if (ok) showSuccess(ok === 1 ? 'File attached.' : `${ok} files attached.`);
  return ok;
}

/** A button that opens the file picker and attaches what is chosen to `target`. */
export function attachButton(target, { label = 'Attach a file', onDone } = {}) {
  const wrap = document.createElement('span');
  wrap.innerHTML = `<button type="button" class="btn btn-secondary btn-sm">${getIcon('paperclip', 14)} ${escapeHtml(label)}</button>
    <input type="file" multiple accept="${ACCEPT}" hidden>`;
  const btn = wrap.querySelector('button');
  const input = wrap.querySelector('input');
  btn.addEventListener('click', () => input.click());
  input.addEventListener('change', async () => {
    const files = [...input.files];
    input.value = '';
    if (!files.length) return;
    btn.disabled = true;
    try {
      if (await uploadAll(files, target)) onDone && onDone();
    } finally { btn.disabled = false; }
  });
  return wrap;
}

/** The list of files plus (for writers) a drop zone. */
export function mountAttachments(box, { entity, customer_code, work_item_id, writer = false, onChange } = {}) {
  if (!box) return;
  const target = { entity, customer_code, work_item_id };
  box.innerHTML = `
    ${writer ? `<div class="sc-drop" data-att-drop tabindex="0" role="button"
        style="border:1px dashed var(--border-color,#d1d5db);border-radius:var(--radius-md,8px);padding:var(--space-3);
               text-align:center;color:var(--text-tertiary);cursor:pointer;margin-bottom:var(--space-2)">
        ${getIcon('upload', 14)} Drop files here or <u>choose files</u>
        <div class="sc-list-meta">PDFs, pictures, Office documents, emails, zips and drawings · up to ${MAX_MB} MB each</div>
        <input type="file" multiple accept="${ACCEPT}" hidden data-att-input></div>` : ''}
    <div data-att-list><div class="spinner" style="margin:var(--space-3) auto"></div></div>`;

  const list = box.querySelector('[data-att-list]');

  async function load() {
    try {
      const params = { entity };
      if (work_item_id) params.work_item = work_item_id; else params.customer = customer_code;
      const res = await salesApi.listAttachments(params);
      const items = res.data || [];
      list.innerHTML = items.length ? `<ul class="sc-att-list" style="list-style:none;padding:0;margin:0">${items.map(a => `
        <li style="display:flex;gap:var(--space-2);align-items:center;padding:4px 0;border-bottom:1px solid var(--border-light,#f1f5f9)">
          <span style="flex:1;min-width:0">${attachmentLink(a)}
            <div class="sc-list-meta">${escapeHtml(shortDate(a.created_at))}${a.created_by && a.created_by.name ? ` · ${escapeHtml(a.created_by.name)}` : ''}${
              a.note ? ` · ${escapeHtml(a.note)}` : ''}</div></span>
          ${writer ? `<button type="button" class="btn btn-ghost btn-sm" data-att-del="${escapeHtml(a.id)}" title="Remove the file">${getIcon('trash-2', 13)}</button>` : ''}
        </li>`).join('')}</ul>`
        : '<p class="sc-list-meta" style="margin:0">No files yet.</p>';
    } catch (err) {
      list.innerHTML = `<p class="sc-list-meta">${getIcon('alert-circle', 12)} The files could not be loaded (${escapeHtml(err.message || 'no answer')}).</p>`;
    }
  }

  async function add(files) {
    if (await uploadAll(files, target)) { load(); onChange && onChange(); }
  }

  const drop = box.querySelector('[data-att-drop]');
  if (drop) {
    const input = drop.querySelector('[data-att-input]');
    drop.addEventListener('click', (e) => { if (e.target !== input) input.click(); });
    drop.addEventListener('keydown', (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); input.click(); } });
    input.addEventListener('change', () => { const f = [...input.files]; input.value = ''; if (f.length) add(f); });
    drop.addEventListener('dragover', (e) => { e.preventDefault(); drop.style.background = 'var(--brand-50,#e6f0f5)'; });
    drop.addEventListener('dragleave', () => { drop.style.background = ''; });
    drop.addEventListener('drop', (e) => {
      e.preventDefault(); drop.style.background = '';
      const f = [...((e.dataTransfer && e.dataTransfer.files) || [])];
      if (f.length) add(f);
    });
  }
  list.addEventListener('click', async (e) => {
    const del = e.target.closest('[data-att-del]');
    if (!del) return;
    if (!window.confirm('Remove this file from the account?')) return;
    del.disabled = true;
    try {
      await salesApi.deleteAttachment(del.dataset.attDel);
      showSuccess('File removed.');
      load(); onChange && onChange();
    } catch (err) {
      showError(err.message || 'The file was not removed.');
      del.disabled = false;
    }
  });
  load();
}
